// Cadastro de Perfil (issue #71: Permissões reais por Perfil) — nome +
// Permissão de Módulo (nível de acesso por Módulo). A aplicação da permissão
// (quem pode ler/gravar onde) mora em `comandos/_acesso.ts`; aqui é só o
// cadastro do Perfil em si. Funções puras sobre o Snapshot.

import { newId, type Id } from "./ids.ts";
import { concedeGravacao, type Modulo, type NivelPermissao } from "./schema.ts";
import type { PermissaoModulo, Perfil, Snapshot } from "./snapshot.ts";

export type { Perfil };

export class ErroPerfil extends Error {}

export interface PermissaoModuloEntrada {
  modulo: Modulo;
  nivel: NivelPermissao;
}

export interface DadosPerfil {
  nome: string;
  permissoes: PermissaoModuloEntrada[];
}

function validarNome(nome: string): string {
  const n = nome.trim();
  if (n === "") throw new ErroPerfil("Informe o nome do perfil.");
  return n;
}

/**
 * Uma linha por Módulo com nível != "nenhum" — a ausência de uma linha já
 * significa "nenhum" (é como `podeGravarNoModulo`/`podeLerModulo` leem hoje),
 * então não há por que guardar linha nenhuma. Um `Map` também descarta
 * duplicidade do mesmo Módulo na entrada (a tela nunca manda duas, mas o
 * domínio não confia nisso).
 */
function linhasPermissao(perfilId: Id, permissoes: PermissaoModuloEntrada[]): PermissaoModulo[] {
  const porModulo = new Map<Modulo, NivelPermissao>();
  for (const p of permissoes) porModulo.set(p.modulo, p.nivel);
  const linhas: PermissaoModulo[] = [];
  for (const [modulo, nivel] of porModulo) {
    if (nivel !== "nenhum") linhas.push({ perfilId, modulo, nivel });
  }
  return linhas;
}

export function criarPerfil(snapshot: Snapshot, dados: DadosPerfil): Snapshot {
  const nome = validarNome(dados.nome);
  const id = newId();
  const perfil: Perfil = { id, nome, ativo: true };
  return {
    ...snapshot,
    perfis: [...snapshot.perfis, perfil],
    permissoesModulo: [
      ...snapshot.permissoesModulo,
      ...linhasPermissao(id, dados.permissoes),
    ],
  };
}

/** Este Perfil tem gravação (ou "ambos") no Módulo Configuração? */
export function temGravacaoEmConfiguracao(
  permissoesModulo: readonly PermissaoModulo[],
  perfilId: Id,
): boolean {
  return permissoesModulo.some(
    (pm) => pm.perfilId === perfilId && pm.modulo === "configuracao" && concedeGravacao(pm.nivel),
  );
}

/** Algum Perfil ATIVO além de `exceto` ainda tem gravação em Configuração? */
function existeOutroAdministrativoAtivo(snapshot: Snapshot, exceto: Id): boolean {
  return snapshot.perfis.some(
    (p) => p.id !== exceto && p.ativo && temGravacaoEmConfiguracao(snapshot.permissoesModulo, p.id),
  );
}

const MSG_ULTIMO_ADMINISTRATIVO =
  "Este é o único perfil ativo com gravação em Configuração — sem ele, ninguém " +
  "conseguiria gerir perfis. Dê essa permissão a outro perfil antes.";

export function editarPerfil(
  snapshot: Snapshot,
  id: Id,
  dados: DadosPerfil,
): Snapshot {
  const atual = snapshot.perfis.find((p) => p.id === id);
  if (!atual) throw new ErroPerfil("Perfil não encontrado.");
  const nome = validarNome(dados.nome);

  // Guarda de "último administrativo" (mesmo espírito de `ativosSem` em
  // usuarios.ts): perde sentido travar só a auto-edição (comandos/perfil.ts
  // já faz isso) se OUTRO Perfil, editado por OUTRA pessoa, puder deixar o
  // sistema inteiro sem nenhum Perfil capaz de gerir Perfis.
  const perdeAdministrativo =
    atual.ativo &&
    temGravacaoEmConfiguracao(snapshot.permissoesModulo, id) &&
    !dados.permissoes.some((p) => p.modulo === "configuracao" && concedeGravacao(p.nivel));
  if (perdeAdministrativo && !existeOutroAdministrativoAtivo(snapshot, id)) {
    throw new ErroPerfil(MSG_ULTIMO_ADMINISTRATIVO);
  }

  return {
    ...snapshot,
    perfis: snapshot.perfis.map((p) => (p.id === id ? { ...p, nome } : p)),
    permissoesModulo: [
      ...snapshot.permissoesModulo.filter((pm) => pm.perfilId !== id),
      ...linhasPermissao(id, dados.permissoes),
    ],
  };
}

export function definirAtivoPerfil(
  snapshot: Snapshot,
  id: Id,
  ativo: boolean,
): Snapshot {
  const atual = snapshot.perfis.find((p) => p.id === id);
  if (!atual) throw new ErroPerfil("Perfil não encontrado.");

  if (
    !ativo &&
    atual.ativo &&
    temGravacaoEmConfiguracao(snapshot.permissoesModulo, id) &&
    !existeOutroAdministrativoAtivo(snapshot, id)
  ) {
    throw new ErroPerfil(MSG_ULTIMO_ADMINISTRATIVO);
  }

  return {
    ...snapshot,
    perfis: snapshot.perfis.map((p) => (p.id === id ? { ...p, ativo } : p)),
  };
}

/** As Permissões de Módulo de um Perfil, como entrada de formulário (uma por Módulo com nível != "nenhum"). */
export function permissoesDoPerfil(
  snapshot: Pick<Snapshot, "permissoesModulo">,
  perfilId: Id,
): PermissaoModuloEntrada[] {
  return snapshot.permissoesModulo
    .filter((pm) => pm.perfilId === perfilId)
    .map((pm) => ({ modulo: pm.modulo, nivel: pm.nivel }));
}
